import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { apiClient } from '@/api/client';
import { useAuthStore, type LoginOut, type UserOut } from '@/stores/auth';

// ---------------------------------------------------------------------------
// API contract (matches backend auth router)
// ---------------------------------------------------------------------------

export interface RegisterIn {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
}

export interface LoginIn {
  email: string;
  password: string;
}

export interface ActivateIn {
  token: string;
}

export interface RefreshIn {
  refresh_token: string;
}

export interface PasswordResetRequestIn {
  email: string;
}

export interface PasswordResetConfirmIn {
  token: string;
  new_password: string;
}

export interface GenericMessage {
  detail: string;
}

export interface ApiErrorBody {
  detail?: string | { msg?: string; code?: string }[];
  code?: string;
}

export type ApiError = AxiosError<ApiErrorBody>;

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
};

// ---------------------------------------------------------------------------
// Raw calls
// ---------------------------------------------------------------------------

async function postLogin(payload: LoginIn): Promise<LoginOut> {
  const { data } = await apiClient.post<LoginOut>('/auth/login', payload);
  return data;
}

async function postRegister(payload: RegisterIn): Promise<GenericMessage> {
  const { data } = await apiClient.post<GenericMessage>('/auth/register', payload);
  return data;
}

async function postActivate(payload: ActivateIn): Promise<GenericMessage> {
  const { data } = await apiClient.post<GenericMessage>('/auth/activate', payload);
  return data;
}

async function postRefresh(payload: RefreshIn): Promise<LoginOut> {
  const { data } = await apiClient.post<LoginOut>('/auth/refresh', payload);
  return data;
}

async function postLogout(): Promise<void> {
  await apiClient.post('/auth/logout');
}

async function postPasswordResetRequest(payload: PasswordResetRequestIn): Promise<GenericMessage> {
  const { data } = await apiClient.post<GenericMessage>('/auth/password-reset', payload);
  return data;
}

async function postPasswordResetConfirm(payload: PasswordResetConfirmIn): Promise<GenericMessage> {
  const { data } = await apiClient.post<GenericMessage>('/auth/password-reset/confirm', payload);
  return data;
}

async function getCurrentUser(): Promise<UserOut> {
  const { data } = await apiClient.get<UserOut>('/auth/me');
  return data;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useLoginMutation() {
  const auth = useAuthStore();
  const qc = useQueryClient();
  return useMutation<LoginOut, ApiError, LoginIn>({
    mutationFn: postLogin,
    onSuccess: (data) => {
      auth.setAuth(data);
      qc.setQueryData(authKeys.me(), data.user);
    },
  });
}

export function useRegisterMutation() {
  return useMutation<GenericMessage, ApiError, RegisterIn>({
    mutationFn: postRegister,
  });
}

export function useActivateMutation() {
  return useMutation<GenericMessage, ApiError, ActivateIn>({
    mutationFn: postActivate,
  });
}

export function useRefreshMutation() {
  const auth = useAuthStore();
  return useMutation<LoginOut, ApiError, RefreshIn>({
    mutationFn: postRefresh,
    onSuccess: (data) => {
      auth.setAuth(data);
    },
  });
}

export function useLogoutMutation() {
  const auth = useAuthStore();
  const qc = useQueryClient();
  return useMutation<void, ApiError, void>({
    mutationFn: postLogout,
    onSettled: () => {
      auth.clearAuth();
      qc.removeQueries({ queryKey: authKeys.all });
    },
  });
}

export function usePasswordResetRequestMutation() {
  return useMutation<GenericMessage, ApiError, PasswordResetRequestIn>({
    mutationFn: postPasswordResetRequest,
  });
}

export function usePasswordResetConfirmMutation() {
  return useMutation<GenericMessage, ApiError, PasswordResetConfirmIn>({
    mutationFn: postPasswordResetConfirm,
  });
}

export function useCurrentUserQuery() {
  const auth = useAuthStore();
  return useQuery<UserOut, ApiError>({
    queryKey: authKeys.me(),
    queryFn: async () => {
      const u = await getCurrentUser();
      auth.setUser(u);
      return u;
    },
    enabled: () => auth.isAuthenticated,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}
